/**
 * Purchase-option selector for the product page.
 *
 * Lets the shopper toggle between a one-time purchase and any selling plan
 * group attached to the product, keeps the product form's `selling_plan` input
 * in sync, and re-prices every option when the variant changes.
 *
 * It listens on the theme's existing `variant-change` pub/sub event so it stays
 * correct when product-info swaps the section markup.
 */

class SellingPlanSelector extends HTMLElement {
  connectedCallback() {
    const payload = this.querySelector('[data-selling-plan-data]');

    try {
      this.data = payload ? JSON.parse(payload.textContent) : {};
    } catch (error) {
      console.error('[subscription] could not parse selling plan data', error);
      this.data = {};
    }

    this.sectionId = this.dataset.section;
    this.formId = this.dataset.formId;
    this.currentVariantId = this.dataset.variantId;

    this.radios = Array.from(this.querySelectorAll('[data-purchase-option]'));
    this.frequency = this.querySelector('[data-selling-plan-frequency]');
    this.frequencyWrapper = this.querySelector('[data-selling-plan-frequency-wrapper]');
    this.input = this.ensureInput();

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);

    if (typeof subscribe === 'function') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data.sectionId !== this.sectionId) return;
        this.currentVariantId = event.data.variant ? String(event.data.variant.id) : null;
        this.repriceOptions();
        this.syncAvailability();
      });
    }

    this.repriceOptions();
    this.syncState();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /**
   * The `selling_plan` input lives inside the product form, which sits in a
   * different block. Create it once and reuse it.
   */
  ensureInput() {
    const form = document.getElementById(this.formId);
    if (!form) return null;

    let input = form.querySelector('input[name="selling_plan"]');

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      form.append(input);
    }

    return input;
  }

  get selectedOption() {
    return this.radios.find((radio) => radio.checked) || this.radios[0];
  }

  onChange(event) {
    if (!event.target.matches('[data-purchase-option], [data-selling-plan-frequency]')) return;
    this.syncState();
  }

  syncState() {
    const option = this.selectedOption;
    if (!option) return;

    const isSubscription = option.value !== 'onetime';

    if (this.frequencyWrapper) {
      this.frequencyWrapper.hidden = !isSubscription || !this.frequency || this.frequency.options.length < 2;
    }

    let sellingPlanId = '';

    if (isSubscription) {
      sellingPlanId = this.frequency && this.frequency.options.length
        ? this.frequency.value
        : option.value;
    }

    if (this.input) this.input.value = sellingPlanId;

    this.dispatchEvent(
      new CustomEvent('selling-plan:change', {
        bubbles: true,
        detail: { sellingPlanId, variantId: this.currentVariantId },
      })
    );
  }

  allocationsFor(variantId) {
    const variant = this.data.variants && this.data.variants[String(variantId)];
    return variant ? variant.allocations || {} : {};
  }

  oneTimePriceFor(variantId) {
    const variant = this.data.variants && this.data.variants[String(variantId)];
    return variant ? variant.price : null;
  }

  repriceOptions() {
    const variantId = this.currentVariantId;
    const allocations = this.allocationsFor(variantId);
    const basePrice = this.oneTimePriceFor(variantId);

    this.querySelectorAll('[data-option-price]').forEach((node) => {
      const planId = node.dataset.optionPrice;

      if (planId === 'onetime') {
        if (basePrice !== null && basePrice !== undefined) node.textContent = this.money(basePrice);
        return;
      }

      const allocation = allocations[planId];
      node.textContent = allocation ? this.money(allocation.price) : '';
    });

    this.querySelectorAll('[data-option-savings]').forEach((node) => {
      const planId = node.dataset.optionSavings;
      const allocation = allocations[planId];

      if (!allocation || !basePrice || allocation.price >= basePrice) {
        node.hidden = true;
        return;
      }

      const saved = basePrice - allocation.price;
      const percent = Math.round((saved / basePrice) * 100);

      node.hidden = false;
      node.textContent = (this.data.strings.savings || '')
        .replace('[[percent]]', percent)
        .replace('[[amount]]', this.money(saved));
    });

    if (this.frequency) {
      Array.from(this.frequency.options).forEach((option) => {
        const allocation = allocations[option.value];
        if (!allocation) return;
        option.textContent = `${option.dataset.label} — ${this.money(allocation.price)}`;
      });
    }
  }

  /** Disable subscription options a variant is not enrolled in. */
  syncAvailability() {
    const allocations = this.allocationsFor(this.currentVariantId);

    this.radios.forEach((radio) => {
      if (radio.value === 'onetime') return;
      const available = Object.prototype.hasOwnProperty.call(allocations, radio.value)
        || Array.from(this.frequency ? this.frequency.options : []).some((option) => allocations[option.value]);

      radio.disabled = !available;
      const label = radio.closest('.purchase-option');
      if (label) label.hidden = !available;

      if (!available && radio.checked) {
        const fallback = this.radios.find((entry) => entry.value === 'onetime');
        if (fallback) {
          fallback.checked = true;
          this.syncState();
        }
      }
    });

    if (this.frequency) {
      Array.from(this.frequency.options).forEach((option) => {
        option.hidden = !allocations[option.value];
      });

      if (this.frequency.selectedOptions[0] && this.frequency.selectedOptions[0].hidden) {
        const firstUsable = Array.from(this.frequency.options).find((option) => !option.hidden);
        if (firstUsable) {
          this.frequency.value = firstUsable.value;
          this.syncState();
        }
      }
    }
  }

  money(cents) {
    if (!this.moneyFormat) {
      const raw = this.data.moneyFormat || '${{amount}}';
      const scratch = document.createElement('textarea');
      scratch.innerHTML = raw;
      this.moneyFormat = scratch.value;
    }

    const format = this.moneyFormat;
    const amount = (Number(cents) / 100).toFixed(2);
    const [whole, fraction] = amount.split('.');
    const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

    return format.replace(/\{\{\s*\w+\s*\}\}/, `${grouped}.${fraction}`);
  }
}

if (!customElements.get('selling-plan-selector')) {
  customElements.define('selling-plan-selector', SellingPlanSelector);
}
